'use client';

import { COLLECTION_ZONE_LAYOUT, PRISMIC_ZONE_LAYOUT, ZONE_SLICE_TYPES } from '@/app/constants/zone';
import { GamesGridZone } from '@/app/components/zones/layouts/games-grid/games-grid-zone';
import { useSimilarGamesZone } from '@/hooks/use-similar-games-zone/use-similar-games-zone';
import { useGlobalContext } from '@/store/contexts/global-context';
import { AMPLITUDE_EXPERIMENTS, isExperimentEnabled } from '@/utils/experiments';
import type { Zone } from '@/types/zones/zone';

const SIMILAR_GAMES_ZONE_NAME = 'Similar Games';
const SIMILAR_GAMES_ZONE_MODULE = 'game_zone';

type SimilarGamesZoneProps = {
    initialZone: Zone;
    isOnlyChildZone: boolean;
    gridLayout?: PRISMIC_ZONE_LAYOUT;
};

const SimilarGamesZoneContent = ({
    initialZone,
    isOnlyChildZone,
    gridLayout = PRISMIC_ZONE_LAYOUT.DEFAULT
}: SimilarGamesZoneProps) => {
    const { isLoggedIn } = useGlobalContext();
    const zoneData = useSimilarGamesZone({ initialZone, gridLayout });

    if (!zoneData) {
        return null;
    }

    const hideGameInfo = gridLayout !== PRISMIC_ZONE_LAYOUT.DEFAULT || isLoggedIn;
    const zoneLayout = COLLECTION_ZONE_LAYOUT[gridLayout];

    return (
        <GamesGridZone
            games={zoneData.games}
            tracking={zoneData.tracking}
            gridLayout={gridLayout}
            isOnlyChildZone={isOnlyChildZone}
            hideGameInfo={hideGameInfo}
            mlRecommendation={zoneData.mlRecommendation}
            meta={{
                title: zoneData.meta.title,
                categoryUid: zoneData.meta.categoryUid,
                rotatingTitles: zoneData.meta.rotatingTitles,
                zoneUid: zoneData.meta.zoneUid ?? initialZone.uid,
                zoneType: ZONE_SLICE_TYPES.SIMILAR_GAMES,
                bannerId: zoneData.meta.zoneUid ?? initialZone.uid,
                bannerName: SIMILAR_GAMES_ZONE_NAME,
                zoneName: SIMILAR_GAMES_ZONE_NAME,
                zoneModule: SIMILAR_GAMES_ZONE_MODULE,
                automatedZone: null,
                zoneLayout,
                gridClassModifier: 'similar-games-zone',
                forwardTileTracking: true
            }}
        />
    );
};

export const SimilarGamesZone = (props: SimilarGamesZoneProps) => {
    const { experiments } = useGlobalContext();

    if (!isExperimentEnabled(experiments, AMPLITUDE_EXPERIMENTS.SIMILAR_GAMES)) {
        return null;
    }

    return <SimilarGamesZoneContent {...props} />;
};
